import React, { useEffect, useState, useContext, useCallback } from 'react';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import styles from './styles.module.css';
import { Button, Grid, TextField, MenuItem, Box, Skeleton } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ShiftService from 'services/ShiftService';
import AccountServices from 'services/AccountServices';
import { AlertMessagesContext } from 'react-alert-messages';
import DataTable from 'components/utils/DataTable';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ShiftPaymentCashBookPopup from 'components/popups/ShiftPaymentCashBookPopup';
import Info from 'components/utils/Alert/Info';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'const';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
const TRANSACTION_TYPES = {
  SALE: 'Sale',
  EXPENSE: 'Expense',
  PAYMENT: 'Payment',
};

function ShiftSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [shiftTransactions, setShiftTransactions] = useState([]);
  const [shiftDetails, setShiftDetails] = useState({});
  const [filteredShiftTransactions, setFilteredShiftTransactions] = useState([]);
  const [filteredShiftDetails, setFilteredShiftDetails] = useState({});
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedCashBook, setSelectedCashBook] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState('all');

  const { shiftId } = useParams();

  const history = useHistory();
  const { shop } = useContext(ShopContext);

  const formatCurrency = (amount) => {
    return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, amount || 0)}`;
  };

  const renderSummarySkeleton = () => (
    <Box className={styles.changeable} sx={{ p: '10px', mb: '10px' }}>
      <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
        <span className={styles.subTitle}>Shift Overview</span>
      </div>
      <table className={styles.summaryTable}>
        <tbody>
          {[...Array(7)].map((_, rowIndex) => (
            <tr key={rowIndex}>
              {[...Array(3)].map((_, colIndex) => (
                <td key={colIndex} style={{ width: '33.333%' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Skeleton variant="text" width="45%" height={20} animation="wave" />
                    <Skeleton variant="text" width="35%" height={20} animation="wave" />
                  </Box>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  const displayShifts = React.useMemo(() => {
    return selectedShift ? [selectedShift] : [];
  }, [selectedShift]);

  const addBalance = (transactions, initialBalance = 0) => {
    let balance = initialBalance;
    const accountedOpeningHeads = new Set();

    const hasOpeningTx = transactions.some(
      (tnx) => tnx.description && tnx.description.toLowerCase().includes('opening balance adjusted')
    );

    if (hasOpeningTx) {
      balance = 0;
    }

    return transactions.map((tnx) => {
      const isOpeningAdj = tnx.description && tnx.description.toLowerCase().includes('opening balance adjusted');
      let amount = 0;

      if (isOpeningAdj) {
        if (tnx.account_head && !accountedOpeningHeads.has(tnx.account_head)) {
          amount = tnx.balance || 0;
          accountedOpeningHeads.add(tnx.account_head);
        } else {
          amount = (tnx.debit || 0) - (tnx.credit || 0);
        }
      } else {
        amount = (tnx.debit || 0) - (tnx.credit || 0);
        if (tnx.description && tnx.description.toLowerCase().includes('opening balance') && tnx.account_head) {
          accountedOpeningHeads.add(tnx.account_head);
        }
      }

      const newBalance = balance + amount;
      balance = newBalance;
      return { ...tnx, balance: newBalance };
    });
  };

  const showViewCashBook = useCallback(
    (data) => {
      const openingBalance = {
        time: selectedShift?.opened_at,
        debit: data?.opening || 0,
        credit: 0,
        description: tReport('Opening balance {{balance}}', { balance: ShopsHelper.getAmountFormatted(shop, data?.opening || 0) }),
        balance: data?.opening_balance || 0,
        account_head: data?.id,
      };

      const transactionsWithBalance = addBalance([openingBalance, ...data.transactions], 0);

      setSelectedCashBook({
        summary: data,
        tnx: transactionsWithBalance,
      });
    },
    [selectedShift, tReport, shop]
  );

  const shiftReportHeaders = React.useMemo(
    () => [
      {
        label: tCommon('Payment'),
        id: 'payment_mode',
        type: 'callback',
        viewRender: (obj) => (
          <span
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              backgroundColor: '#e2f0d9',
              color: '#385723',
              fontWeight: 'bold',
              fontSize: '13px',
              display: 'inline-block',
            }}
          >
            {obj?.payment_mode?.name?.toUpperCase()}
          </span>
        ),
      },
      {
        label: tCommon('Opening'),
        id: 'opening',
        type: 'callback',
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.opening ?? 0),
      },
      {
        label: '',
        id: 'blank_col',
        type: 'callback',
        viewRender: () => '',
      },
      {
        label: tCommon('In'),
        id: 'cash_in',
        type: 'callback',
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.cash_in || item.transactions.reduce((sum, sp) => sum + (sp.debit || 0), 0)),
      },
      {
        label: tCommon('Out'),
        id: 'cash_out',
        type: 'callback',
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.cash_out || item.transactions.reduce((sum, sp) => sum + (sp.credit || 0), 0)),
      },
      {
        label: tCommon('Closing'),
        id: 'closing',
        type: 'callback',
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.closing ?? 0),
      },
      {
        label: tCommon('Action'),
        id: 'deleteBtn',
        type: 'callback',
        align: 'right',
        viewRender: (obj) => (
          <span
            onClick={() => showViewCashBook(obj)}
            style={{
              color: '#4662d9',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            <EventNoteIcon style={{ fontSize: '18px' }} />
            View more
          </span>
        ),
      },
    ],
    [showViewCashBook, tCommon, shop]
  );

  const shiftJournalHeaders = [
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.getDate(item.time),
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.convertTo12HourTime(item.time),
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'callback',
      viewRender: (item) => {
        return item.account_head_name ? `[${item.account_head_name}] ${item.description || ''}` : item.description;
      },
    },
    {
      label: tCommon('In'),
      id: 'debit',
      type: 'floatAmount',
    },
    {
      label: tCommon('Out'),
      id: 'credit',
      type: 'floatAmount',
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'floatAmount',
    },
  ];

  const handleCloseCashBook = () => setSelectedCashBook(null);

  const handleTransactionFilter = (type) => {
    setTransactionFilter(type);
    if (type === 'all') {
      setFilteredShiftTransactions(shiftTransactions);
      setFilteredShiftDetails(shiftDetails);
    } else {
      const filteredTransactions = shiftTransactions.filter((item) => item.type === type);
      setFilteredShiftTransactions(filteredTransactions);
      const filteredDetails = {
        ...shiftDetails,
        shift_payments: shiftDetails.shift_payments.map((sp) => {
          return {
            ...sp,
            transactions: sp.transactions.filter((sr) => sr.type === type),
          };
        }),
      };
      setFilteredShiftDetails(filteredDetails);
    }
  };

  useEffect(() => {
    const loadAllShiftData = async () => {
      setLoadingIndicator(true);
      try {
        const [shiftDetailsResp, shiftTransactionsResp, accountHeadsData] = await Promise.all([
          ShiftService.getShift(shiftId),
          ShiftService.getShiftTransactions(shiftId),
          AccountServices.getAccountHeads(),
        ]);

        setSelectedShift(shiftDetailsResp);

        const heads = (accountHeadsData.results || accountHeadsData || []).filter(
          (h) => h.type === 'PAYMENT_MODE'
        );

        const paymentModesWithLedger = heads.map((head) => {
          const modeTx = (shiftTransactionsResp || []).filter(
            (sr) => sr.account_head === head.id
          );

          // Find if there is a shift payment record from backend
          const sp = (shiftDetailsResp.shift_payments || []).find(
            (p) => p.payment_mode?.id === head.id
          );

          let opening = 0;

          if (sp) {
            opening = sp.opening || 0;
          } else if (modeTx.length > 0) {
            const sortedModeTx = [...modeTx].sort((a, b) => new Date(a.time) - new Date(b.time) || a.id - b.id);
            opening = (sortedModeTx[0].balance || 0) - (sortedModeTx[0].debit || 0) + (sortedModeTx[0].credit || 0);
          }

          const cash_in = modeTx.reduce((sum, t) => sum + (t.debit || 0), 0);
          const cash_out = modeTx.reduce((sum, t) => sum + (t.credit || 0), 0);
          const closing = opening + cash_in - cash_out;

          return {
            id: head.id,
            payment_mode: head,
            opening,
            cash_in,
            cash_out,
            closing,
            transactions: modeTx,
          };
        });

        const shift = {
          ...shiftDetailsResp,
          shift_payments: paymentModesWithLedger,
        };

        setShiftDetails(shift);
        setFilteredShiftDetails(shift);

        const allLedgerTnx = paymentModesWithLedger.flatMap((pm) => pm.transactions || []);
        if (allLedgerTnx.length > 0) {
          allLedgerTnx.sort((a, b) => new Date(a.time) - new Date(b.time));
          const totalOpeningBalance = paymentModesWithLedger.reduce((sum, pm) => sum + (pm.opening || 0), 0);
          const tnxWithBalance = addBalance(allLedgerTnx, totalOpeningBalance);
          setShiftTransactions(tnxWithBalance);
          setFilteredShiftTransactions(tnxWithBalance);
        } else {
          const tnx = addBalance(shiftTransactionsResp);
          setShiftTransactions(tnx);
          setFilteredShiftTransactions(tnx);
        }
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    };

    loadAllShiftData();
  }, [shiftId, postAlertMessage, tReport]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>

        <div className={styles.filterDiv} style={{ marginRight: '15px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Shift')}</label>
          </div>
          <div>
            <TextField
              select
              name="shiftSelect"
              size="small"
              variant="outlined"
              value={selectedShift?.id || shiftId || ''}
              color="primary"
              style={{ minWidth: '220px' }}
              onChange={(event) => {
                const selectedId = event.target.value;
                history.push(ROUTES.SHIFT_SUMMARY.replace(':shiftId', selectedId));
              }}
            >
              {displayShifts.map((shift) => (
                <MenuItem key={shift.id} value={shift.id}>
                  {`${shift.name} (#${shift.id}) - ${DateTimeHelpers.formatDateTime(shift.opened_at)}`}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
        <div className={styles.filterDiv}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Tnx type')}</label>
          </div>
          <div>
            <TextField
              select
              name="tnxType"
              size="small"
              variant="outlined"
              defaultValue={transactionFilter}
              color="primary"
              className={styles.textfeild}
              style={{ marginRight: '10px' }}
              onChange={(event) => {
                handleTransactionFilter(event.target.value);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {Object.keys(TRANSACTION_TYPES).map((type) => (
                <MenuItem key={type} value={type}>
                  {TRANSACTION_TYPES[type]}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={window.print}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
      </div>
      {loadingIndicator ? (
        renderSummarySkeleton()
      ) : (
        selectedShift && (
          <Box className={styles.changeable} sx={{ p: '10px', mb: '10px' }}>
          {/* Section 1: Header / Metadata */}
          <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
            <span className={styles.subTitle}>Shift Overview</span>
          </div>

          {/* Section 2: Overview Grid Table */}
          <table className={styles.summaryTable}>
            <tbody>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Name</span>
                    <span className={styles.cellValue}>{selectedShift?.name || '-'}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Shift ID</span>
                    <span className={styles.cellValue}>{selectedShift?.id || '-'}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Status</span>
                    <span
                      className={styles.cellValue}
                      style={{
                        color: selectedShift?.closed_at ? '#d32f2f' : '#2e7d32',
                        fontWeight: 700,
                      }}
                    >
                      {selectedShift?.closed_at ? 'CLOSED' : 'OPEN'}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Terminal ID</span>
                    <span className={styles.cellValue}>{selectedShift?.terminal?.id || '-'}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Terminal Name</span>
                    <span className={styles.cellValue}>{selectedShift?.terminal?.name || '-'}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Cashier</span>
                    <span className={styles.cellValue}>{selectedShift?.cashier?.name || '-'}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Opened At</span>
                    <span className={styles.cellValue}>
                      {selectedShift?.opened_at ? DateTimeHelpers.formatDateTime(selectedShift.opened_at) : '-'}
                    </span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Closed At</span>
                    <span className={styles.cellValue}>
                      {selectedShift?.closed_at ? DateTimeHelpers.formatDateTime(selectedShift.closed_at) : '-'}
                    </span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>No. of Invoices</span>
                    <span className={styles.cellValue}>
                      {shiftDetails?.no_of_invoice ?? shiftTransactions.filter((item) => item.type === 'SALE').length}
                    </span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Opening Balance</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_opening)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Closing Balance</span>
                    <span className={styles.cellValue}>
                      {formatCurrency(
                        (shiftDetails?.shift_payments || []).reduce((sum, sp) => sum + (sp.closing || 0), 0)
                      )}
                    </span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Sale</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_sale)}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Purchase</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_purchase)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Expenses</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_expense)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Discount</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_discount)}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Round Off</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_round_off)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Vendor Payment</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_vendor_payment)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Total Stock Damage</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_stock_damage)}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Credit Sales</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_credit_sale)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Loyalty Sale</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_loyalty_sale)}</span>
                  </div>
                </td>
                <td>
                  <div className={styles.cellContent}>
                    <span className={styles.cellLabel}>Credit Received</span>
                    <span className={styles.cellValue}>{formatCurrency(shiftDetails?.total_credit_received)}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      )
      )}

      <Grid className={styles.changeable}>
        <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
          <span className={styles.subTitle}>{tCommon('Shift payments')}</span>
        </div>
        {(loadingIndicator || (filteredShiftDetails?.shift_payments || []).length > 0) && (
          <DataTable
            columns={shiftReportHeaders}
            rows={filteredShiftDetails?.shift_payments || []}
            isLoading={loadingIndicator}
          />
        )}
      </Grid>

      <Grid className={styles.changeable}>
        <div className={styles.headTitle} style={{ marginBottom: '10px' }}>
          <span className={styles.subTitle}>{tCommon('Shift journals')}</span>
        </div>
        <DataTable columns={shiftJournalHeaders} rows={filteredShiftTransactions || []} isLoading={loadingIndicator} />
        {!loadingIndicator && filteredShiftTransactions.length === 0 && (
          <Info
            title={tReport('No transactions found')}
            content={tReport('There are no transactions to display for the current shift.')}
          />
        )}
      </Grid>
      {selectedCashBook && (
        <ShiftPaymentCashBookPopup rowData={selectedCashBook} toggleViewCashBook={handleCloseCashBook} />
      )}
    </div>
  );
}

export default withConsoleBase(ShiftSummary);
